/** Explicit, opaque persistence for native Vector documents. */
export const MAX_VECTOR_DOCUMENT_BYTES = 8 * 1024 * 1024;

const DATABASE_NAME = 'tijgr.vector.documents';
const DATABASE_VERSION = 1;
const STORE_NAME = 'documents';
const MAX_NAME_LENGTH = 160;

export type VectorDocumentMetadata = {
  version: 1;
  id: string;
  name: string;
  revision: number;
  createdAt: number;
  updatedAt: number;
  format: 'vector-native';
  byteLength: number;
};

export type VectorDocumentStoreOptions = { signal?: AbortSignal };
export type VectorDocumentStoreErrorCode = 'invalid' | 'unavailable' | 'quota' | 'aborted' | 'conflict' | 'not-found' | 'incompatible';

export class VectorDocumentStoreError extends Error {
  readonly code: VectorDocumentStoreErrorCode;
  constructor(code: VectorDocumentStoreErrorCode, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'VectorDocumentStoreError';
    this.code = code;
  }
}

export type VectorDocumentStore = {
  list(options?: VectorDocumentStoreOptions): Promise<VectorDocumentMetadata[]>;
  read(id: string, options?: VectorDocumentStoreOptions): Promise<{ metadata: VectorDocumentMetadata; bytes: Uint8Array }>;
  create(name: string, bytes: Uint8Array, options?: VectorDocumentStoreOptions): Promise<VectorDocumentMetadata>;
  update(id: string, expectedRevision: number, bytes: Uint8Array, options?: VectorDocumentStoreOptions): Promise<VectorDocumentMetadata>;
  rename(id: string, expectedRevision: number, name: string, options?: VectorDocumentStoreOptions): Promise<VectorDocumentMetadata>;
  delete(id: string, expectedRevision: number, options?: VectorDocumentStoreOptions): Promise<void>;
  close(): void;
};

type StoredDocument = VectorDocumentMetadata & { bytes: ArrayBuffer };
type Operation = { controller: AbortController; unlink: () => void };

function error(code: VectorDocumentStoreErrorCode, message: string, cause?: unknown): VectorDocumentStoreError {
  return new VectorDocumentStoreError(code, message, cause === undefined ? undefined : { cause });
}

function mapFailure(cause: unknown): VectorDocumentStoreError {
  if (cause instanceof VectorDocumentStoreError) return cause;
  const name = cause instanceof DOMException ? cause.name : '';
  if (name === 'QuotaExceededError') return error('quota', 'Browser storage quota was exceeded.', cause);
  if (name === 'AbortError') return error('aborted', 'The storage operation was aborted.', cause);
  if (name === 'VersionError') return error('incompatible', 'The saved Vector database uses a newer version.', cause);
  return error('unavailable', 'Browser document storage is unavailable.', cause);
}

function normalizeName(value: string): string {
  if (typeof value !== 'string') throw error('invalid', 'Document name must be text.');
  const name = value.trim();
  if (!name || name.length > MAX_NAME_LENGTH) throw error('invalid', `Document name must contain 1 to ${MAX_NAME_LENGTH} characters.`);
  return name;
}

function validateId(id: string): void {
  if (typeof id !== 'string' || id.length < 1 || id.length > 128) throw error('invalid', 'Document id is invalid.');
}

function validateRevision(revision: number): void {
  if (!Number.isSafeInteger(revision) || revision < 1) throw error('invalid', 'Expected revision must be a positive safe integer.');
}

function copyBytes(bytes: Uint8Array): ArrayBuffer {
  if (!(bytes instanceof Uint8Array)) throw error('invalid', 'Document bytes must be a Uint8Array.');
  if (bytes.byteLength < 1) throw error('invalid', 'Document bytes must not be empty.');
  if (bytes.byteLength > MAX_VECTOR_DOCUMENT_BYTES) throw error('invalid', `Document exceeds the ${MAX_VECTOR_DOCUMENT_BYTES}-byte limit.`);
  // Copy synchronously before the operation can yield, including when the view is detached later.
  return bytes.slice().buffer;
}

function validMetadata(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<VectorDocumentMetadata>;
  return v.version === 1 && typeof v.id === 'string' && v.id.length > 0 && v.id.length <= 128
    && typeof v.name === 'string' && v.name.length > 0 && v.name.length <= MAX_NAME_LENGTH
    && Number.isSafeInteger(v.revision) && (v.revision ?? 0) > 0
    && Number.isFinite(v.createdAt) && Number.isFinite(v.updatedAt)
    && v.format === 'vector-native' && Number.isSafeInteger(v.byteLength)
    && (v.byteLength ?? 0) >= 1 && (v.byteLength ?? Infinity) <= MAX_VECTOR_DOCUMENT_BYTES;
}

function validateStored(value: unknown): StoredDocument {
  if (!value || typeof value !== 'object') throw error('incompatible', 'A saved Vector document record is malformed.');
  const record = value as Partial<StoredDocument>;
  if (!validMetadata(record as unknown) || !(record.bytes instanceof ArrayBuffer) || record.bytes.byteLength !== record.byteLength) {
    throw error('incompatible', 'A saved Vector document record is malformed or incompatible.');
  }
  return record as StoredDocument;
}

/** Create a lazy adapter. It does not inspect or open browser storage until a method is called. */
export function createVectorDocumentStore(): VectorDocumentStore {
  const operations = new Set<Operation>();

  function begin(options?: VectorDocumentStoreOptions): Operation {
    const controller = new AbortController();
    const signal = options?.signal;
    if (signal && signal.aborted) controller.abort();
    const relay = () => controller.abort();
    signal?.addEventListener('abort', relay, { once: true });
    const op: Operation = { controller, unlink: () => signal?.removeEventListener('abort', relay) };
    operations.add(op);
    return op;
  }

  async function open(signal: AbortSignal): Promise<IDBDatabase> {
    if (signal.aborted) throw error('aborted', 'The storage operation was aborted.');
    if (typeof indexedDB === 'undefined') throw error('unavailable', 'IndexedDB is not available in this context.');
    return new Promise((resolve, reject) => {
      let settled = false;
      let timer: ReturnType<typeof setTimeout>;
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      const cleanup = () => { clearTimeout(timer); signal.removeEventListener('abort', abort); };
      const fail = (reason: VectorDocumentStoreError) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(reason);
      };
      const abort = () => fail(error('aborted', 'The storage operation was aborted.'));
      signal.addEventListener('abort', abort, { once: true });
      timer = setTimeout(() => fail(error('unavailable', 'Opening browser storage was blocked or timed out.')), 3000);
      request.onupgradeneeded = event => {
        if (settled || signal.aborted) { request.transaction?.abort(); return; }
        if ((event as IDBVersionChangeEvent).oldVersion === 0) request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
        else if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          fail(error('incompatible', 'The saved Vector database is missing its document store.'));
          request.transaction?.abort();
        }
      };
      request.onblocked = () => { /* bounded by timer; another tab may still release the upgrade */ };
      request.onerror = () => fail(mapFailure(request.error));
      request.onsuccess = () => {
        const db = request.result;
        if (settled || signal.aborted) { db.close(); return; }
        if (!db.objectStoreNames.contains(STORE_NAME)) { db.close(); fail(error('incompatible', 'The saved Vector database is missing its document store.')); return; }
        try {
          const store = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME);
          if (store.keyPath !== 'id' || store.autoIncrement) {
            db.close(); fail(error('incompatible', 'The saved Vector database has an incompatible document store schema.')); return;
          }
        } catch (cause) { db.close(); fail(error('incompatible', 'The saved Vector database has an incompatible document store schema.', cause)); return; }
        settled = true;
        cleanup();
        resolve(db);
      };
    });
  }

  async function run<T>(options: VectorDocumentStoreOptions | undefined, work: (db: IDBDatabase, signal: AbortSignal) => Promise<T>): Promise<T> {
    const op = begin(options);
    let db: IDBDatabase | undefined;
    try {
      if (op.controller.signal.aborted) throw error('aborted', 'The storage operation was aborted.');
      db = await open(op.controller.signal);
      if (op.controller.signal.aborted) throw error('aborted', 'The storage operation was aborted.');
      return await work(db, op.controller.signal);
    } catch (cause) {
      throw mapFailure(cause);
    } finally {
      db?.close();
      op.unlink();
      operations.delete(op);
    }
  }

  function transaction<T>(db: IDBDatabase, mode: IDBTransactionMode, signal: AbortSignal, action: (store: IDBObjectStore, finish: (value: T) => void, guard: (callback: () => void) => () => void) => void): Promise<T> {
    return new Promise((resolve, reject) => {
      let result: T;
      let hasResult = false;
      let done = false;
      let actionFailure: unknown;
      let tx: IDBTransaction;
      try { tx = db.transaction(STORE_NAME, mode); }
      catch (cause) { reject(mapFailure(cause)); return; }
      const abort = () => { try { tx.abort(); } catch { /* already inactive */ } };
      const fail = (cause: unknown) => {
        if (done) return;
        done = true;
        signal.removeEventListener('abort', abort);
        reject(mapFailure(cause));
      };
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) abort();
      tx.oncomplete = () => {
        if (done) return;
        done = true;
        signal.removeEventListener('abort', abort);
        if (!hasResult) reject(error('unavailable', 'Storage transaction completed without a result.'));
        else resolve(result);
      };
      tx.onabort = () => fail(actionFailure ?? tx.error ?? error('aborted', 'The storage transaction was aborted.'));
      tx.onerror = () => { /* onabort reports the final transaction failure */ };
      const guard = (callback: () => void) => () => {
        try { callback(); }
        catch (cause) { actionFailure = cause; try { tx.abort(); } catch { /* already inactive */ } }
      };
      try { action(tx.objectStore(STORE_NAME), value => { result = value; hasResult = true; }, guard); }
      catch (cause) { try { tx.abort(); } catch { /* rejected by abort completion */ } fail(cause); }
    });
  }

  function requestFailure(request: IDBRequest): never { throw mapFailure(request.error); }

  function compare(record: StoredDocument, revision: number, increment = true): void {
    if (record.revision !== revision) throw error('conflict', 'Document revision changed; reload before saving.');
    if (increment && revision >= Number.MAX_SAFE_INTEGER) throw error('invalid', 'Document revision cannot be incremented safely.');
  }

  return {
    list(options) {
      return run(options, (db, signal) => transaction(db, 'readonly', signal, (store, finish, guard) => {
        const metadata: VectorDocumentMetadata[] = [];
        const request = store.openCursor();
        request.onerror = guard(() => requestFailure(request));
        request.onsuccess = guard(() => {
          const cursor = request.result;
          if (!cursor) { finish(metadata.sort((a, b) => a.updatedAt - b.updatedAt || a.id.localeCompare(b.id))); return; }
          metadata.push(metadataOf(validateStored(cursor.value)));
          cursor.continue();
        });
      }));
    },
    read(id, options) {
      validateId(id);
      return run(options, (db, signal) => transaction(db, 'readonly', signal, (store, finish, guard) => {
        const request = store.get(id);
        request.onerror = guard(() => requestFailure(request));
        request.onsuccess = guard(() => {
          if (request.result === undefined) throw error('not-found', 'Saved Vector document was not found.');
          const record = validateStored(request.result);
          finish({ metadata: metadataOf(record), bytes: new Uint8Array(record.bytes.slice(0)) });
        });
      }));
    },
    create(name, bytes, options) {
      const normalized = normalizeName(name);
      const ownedBytes = copyBytes(bytes);
      return run(options, (db, signal) => transaction(db, 'readwrite', signal, (store, finish, guard) => {
        if (!globalThis.crypto?.randomUUID) throw error('unavailable', 'Secure UUID generation is unavailable.');
        const now = Date.now();
        const record: StoredDocument = { version: 1, id: crypto.randomUUID(), name: normalized, revision: 1, createdAt: now, updatedAt: now, format: 'vector-native', byteLength: ownedBytes.byteLength, bytes: ownedBytes };
        const request = store.add(record);
        request.onerror = guard(() => requestFailure(request));
        request.onsuccess = () => finish(metadataOf(record));
      }));
    },
    update(id, expectedRevision, bytes, options) {
      validateId(id); validateRevision(expectedRevision);
      const ownedBytes = copyBytes(bytes);
      return run(options, (db, signal) => transaction(db, 'readwrite', signal, (store, finish, guard) => {
        const get = store.get(id);
        get.onerror = guard(() => requestFailure(get));
        get.onsuccess = guard(() => {
          if (get.result === undefined) throw error('not-found', 'Saved Vector document was not found.');
          const previous = validateStored(get.result); compare(previous, expectedRevision);
          const record: StoredDocument = { ...previous, revision: previous.revision + 1, updatedAt: Date.now(), byteLength: ownedBytes.byteLength, bytes: ownedBytes };
          const put = store.put(record);
          put.onerror = guard(() => requestFailure(put));
          put.onsuccess = () => finish(metadataOf(record));
        });
      }));
    },
    rename(id, expectedRevision, name, options) {
      validateId(id); validateRevision(expectedRevision);
      const normalized = normalizeName(name);
      return run(options, (db, signal) => transaction(db, 'readwrite', signal, (store, finish, guard) => {
        const get = store.get(id);
        get.onerror = guard(() => requestFailure(get));
        get.onsuccess = guard(() => {
          if (get.result === undefined) throw error('not-found', 'Saved Vector document was not found.');
          const previous = validateStored(get.result); compare(previous, expectedRevision);
          const record: StoredDocument = { ...previous, name: normalized, revision: previous.revision + 1, updatedAt: Date.now() };
          const put = store.put(record);
          put.onerror = guard(() => requestFailure(put));
          put.onsuccess = () => finish(metadataOf(record));
        });
      }));
    },
    delete(id, expectedRevision, options) {
      validateId(id); validateRevision(expectedRevision);
      return run(options, (db, signal) => transaction(db, 'readwrite', signal, (store, finish, guard) => {
        const get = store.get(id);
        get.onerror = guard(() => requestFailure(get));
        get.onsuccess = guard(() => {
          if (get.result === undefined) throw error('not-found', 'Saved Vector document was not found.');
          const previous = validateStored(get.result); compare(previous, expectedRevision, false);
          const request = store.delete(id);
          request.onerror = guard(() => requestFailure(request));
          request.onsuccess = () => finish(undefined);
        });
      }));
    },
    close() {
      for (const op of [...operations]) op.controller.abort();
    },
  };
}

function metadataOf(record: StoredDocument): VectorDocumentMetadata {
  return {
    version: record.version, id: record.id, name: record.name, revision: record.revision,
    createdAt: record.createdAt, updatedAt: record.updatedAt, format: record.format, byteLength: record.byteLength,
  };
}
